import { useState, useEffect } from "react";
import Checkbox from "components/Checkbox";
import Button from "components/Button";
import ContactsTable from "../ContactsTable";
import useContactSelection from "../../hooks/useContactSelection";
import styles from "./ContactSelectionPanel.module.scss";

const ContactSelectionPanel = ({
  initialSelectedContacts = [],
  onSelectionChange,
  enableSelectedContactsPagination = false,
  autoFetchOnMount = true,
  showSelectedPanel = true,
  className,
  sourceIds = [],
}) => {
  const [hasRecentActivity, setHasRecentActivity] = useState(true);

  const contactSelection = useContactSelection(initialSelectedContacts, {
    enableSelectedContactsPagination,
    autoFetchOnMount,
    hasRecentActivity,
    sourceIds,
  });

  const {
    searchQuery,
    setSearchQuery,
    selectedSearchQuery,
    setSelectedSearchQuery,
    selectedContactIds,
    allContacts,
    selectedContactsData,
    selectedContactsLoading,
    selectedContactsHasMore,
    availableContactsCount,
    latestBatchCount,
    allVisibleSelected,
    contactsLoading,
    contactsError,
    hasMore,
    currentOffset,
    handleLoadMore,
    handleLoadMoreSelected,
    handleContactToggle,
    handleSelectAllVisible,
    handleRemoveContact,
    // Sort state & handlers - available panel
    sortDirection,
    sortPeriod,
    handleSortClick,
    handlePeriodChange,
    // Sort state & handlers - selected panel
    selectedSortDirection,
    selectedSortPeriod,
    handleSelectedSortClick,
    handleSelectedPeriodChange,
  } = contactSelection;

  // Notify parent component when selection changes
  useEffect(() => {
    if (onSelectionChange) {
      onSelectionChange(Array.from(selectedContactIds));
    }
  }, [selectedContactIds, onSelectionChange]);

  // Filter available contacts (only filter out invalid IDs, keep selected ones visible)
  const availableContacts = allContacts.filter(
    (contact) => contact.id !== null && contact.id !== undefined,
  );

  return (
    <div className={`${styles.contactSelectionPanel} ${className || ""}`}>
      <div className={styles.mainContent}>
        {/* Available Contacts Panel */}
        <div className={styles.availablePanel}>
          <ContactsTable
            title="Available Contacts"
            searchQuery={searchQuery}
            onSearchChange={(e) => setSearchQuery(e.target.value)}
            showSearch={true}
            contacts={availableContacts}
            selectedContactIds={selectedContactIds}
            onContactToggle={handleContactToggle}
            contactsLoading={contactsLoading}
            contactsError={contactsError}
            hasMore={hasMore}
            onLoadMore={handleLoadMore}
            sortDirection={sortDirection}
            sortPeriod={sortPeriod}
            onSortClick={handleSortClick}
            onPeriodChange={handlePeriodChange}
            headerActions={
              <div className={styles.headerActions}>
                <Checkbox
                  id="has-recent-activity"
                  label="Only contacts with recent activity"
                  value={hasRecentActivity}
                  onChange={(e) => setHasRecentActivity(e.target.checked)}
                  className={styles.filterCheckbox}
                />
                <Button
                  type="button"
                  size="small"
                  style="secondary"
                  onClick={handleSelectAllVisible}
                  className={styles.addButton}
                  disabled={latestBatchCount === 0}
                >
                  {currentOffset === 0
                    ? `Add ${latestBatchCount}`
                    : `Add All (${availableContactsCount})`}
                </Button>
              </div>
            }
            emptyStateMessage="No contacts available"
            className={styles.tablePanel}
          />
        </div>

        {/* Selected Contacts Panel */}
        {showSelectedPanel && (
          <div className={styles.selectedPanel}>
            <ContactsTable
              title={`Selected Contacts (${selectedContactIds.size})`}
              showSearch={true}
              searchQuery={selectedSearchQuery}
              onSearchChange={(e) => setSelectedSearchQuery(e.target.value)}
              contacts={selectedContactsData}
              selectedContactIds={selectedContactIds}
              onContactRemove={handleRemoveContact}
              contactsLoading={selectedContactsLoading}
              hasMore={selectedContactsHasMore}
              onLoadMore={handleLoadMoreSelected}
              sortDirection={selectedSortDirection}
              sortPeriod={selectedSortPeriod}
              onSortClick={handleSelectedSortClick}
              onPeriodChange={handleSelectedPeriodChange}
              emptyStateMessage="No contacts selected"
              isSelectedContactsMode={true}
              className={styles.tablePanel}
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default ContactSelectionPanel;
